/**
 * File Streaming API Models and Interfaces
 * 
 * @remarks
 * Type definitions for the File Streaming, Access Token, and Conversion APIs.
 * These models correspond to the backend API endpoints documented in:
 * - FILE_STREAMING_API.md
 * - FILE_ACCESS_TOKEN_API.md
 * - FILE_CONVERSION_API_DOCUMENTATION.md
 * 
 * @see FILE_STREAMING_API.md
 * @see FILE_ACCESS_TOKEN_API.md
 * @see FILE_CONVERSION_API_DOCUMENTATION.md
 */

// ============================================================================
// File Streaming Models
// ============================================================================

/**
 * Options for file streaming requests
 */
export interface StreamOptions {
  /**
   * Start byte position for range request
   */
  start?: number;

  /**
   * End byte position for range request
   */
  end?: number;

  /**
   * Report download progress events
   * @default false
   */
  reportProgress?: boolean;

  /**
   * Response type for the stream
   * @default 'blob'
   */
  responseType?: 'blob' | 'arraybuffer';
}

/**
 * Metadata extracted from file stream response headers
 */
export interface FileStreamMetadata {
  /**
   * Original file name from Content-Disposition header
   */
  fileName: string;

  /**
   * MIME type from Content-Type header
   */
  fileType: string;

  /**
   * Total file size in bytes (from Content-Length or Content-Range)
   */
  fileSize?: number;

  /**
   * Content-Range header value for partial responses
   * @example "bytes 0-1023/1048576"
   */
  contentRange?: string;
}

/**
 * Combined file stream result with metadata
 */
export interface FileStreamResult {
  /**
   * File binary data as Blob
   */
  blob: Blob;

  /**
   * Extracted metadata from response headers
   */
  metadata: FileStreamMetadata;
}

// ============================================================================
// Access Token Models
// ============================================================================

/**
 * Request to generate a file access token
 */
export interface GenerateAccessTokenRequest {
  /**
   * Token expiration time in seconds
   * @minimum 60
   * @maximum 3600
   */
  expiresIn: number;
}

/**
 * Response from access token generation
 */
export interface AccessTokenResponse {
  /**
   * JWT access token
   */
  accessToken: string;

  /**
   * ISO 8601 timestamp when token expires
   */
  expiresAt: string;

  /**
   * Token lifetime in seconds
   */
  expiresIn: number;
}

/**
 * Individual file access token record
 */
export interface FileAccessToken {
  /**
   * Token record ID
   */
  id: string;

  /**
   * User ID who generated the token
   */
  userId: string;

  /**
   * ISO 8601 timestamp when token expires
   */
  expiresAt: string;

  /**
   * ISO 8601 timestamp when token was created
   */
  createdAt: string;

  /**
   * IP address where token was generated (if available)
   */
  ipAddress?: string;
}

/**
 * Response containing all active tokens for a file
 */
export interface FileAccessTokensResponse {
  /**
   * File ID
   */
  fileId: string;

  /**
   * Total number of active tokens
   */
  count: number;

  /**
   * List of active tokens
   */
  tokens: FileAccessToken[];
}

/**
 * Response from revoking tokens
 */
export interface RevokeTokensResponse {
  /**
   * File ID
   */
  fileId: string;

  /**
   * Number of tokens revoked
   */
  revokedCount: number;
}

// ============================================================================
// File Conversion Models
// ============================================================================

/**
 * Supported conversion formats
 */
export type ConversionFormat = 'html' | 'pdf' | 'text';

/**
 * Options for file conversion
 */
export interface ConversionOptions {
  /**
   * Target conversion format
   */
  format: ConversionFormat;

  /**
   * Page/sheet number for multi-page documents (1-based)
   * Use with Excel files to select specific sheets
   */
  page?: number;

  /**
   * Enable/disable result caching
   * @default true
   */
  cache?: boolean;
}

/**
 * Metadata about the converted file
 */
export interface ConversionMetadata {
  /**
   * Original file size in bytes
   */
  fileSize?: number;

  /**
   * Original file name
   */
  originalFileName?: string;

  /**
   * Total number of pages/slides
   */
  pageCount?: number;

  /**
   * Total number of sheets (for Excel files)
   */
  sheetCount?: number;
}

/**
 * Result of a file conversion operation
 */
export interface ConversionResult {
  /**
   * Original file ID
   */
  fileId: string;

  /**
   * Original MIME type
   */
  originalType: string;

  /**
   * Target format used for conversion
   */
  convertedFormat: ConversionFormat;

  /**
   * Converted content (HTML string, text, or base64 PDF)
   */
  content: string;

  /**
   * Total number of pages/sheets in the document
   */
  pages?: number;

  /**
   * Whether result was retrieved from cache
   */
  cached: boolean;

  /**
   * ISO 8601 timestamp of conversion
   */
  convertedAt: string;

  /**
   * Additional file metadata
   */
  metadata?: ConversionMetadata;
}

// ============================================================================
// Generic API Response Wrapper
// ============================================================================

/**
 * Standard API response wrapper
 */
export interface ApiResponse<T> {
  /**
   * Indicates if the operation was successful
   */
  success: boolean;

  /**
   * Response data (present on success)
   */
  data?: T;

  /**
   * Success or info message
   */
  message?: string;

  /**
   * Error message (present on failure)
   */
  error?: string;
}

// ============================================================================
// Error Response Models
// ============================================================================

/**
 * Validation error detail
 */
export interface ValidationError {
  field: string;
  message: string;
  value?: any;
}

/**
 * Error response from API
 */
export interface ErrorResponse {
  /**
   * Always "error" for error responses
   */
  status: 'error';

  /**
   * Error message
   */
  message: string;

  /**
   * HTTP status code
   */
  statusCode?: number;

  /**
   * ISO 8601 timestamp of error
   */
  timestamp?: string;

  /**
   * Validation errors (for 400 responses)
   */
  validationErrors?: ValidationError[];
}

// ============================================================================
// File Type Information
// ============================================================================

/**
 * Supported file type with conversion capabilities
 */
export interface FileTypeInfo {
  /**
   * MIME type
   */
  mimeType: string;

  /**
   * File extension (without dot)
   */
  extension: string;

  /**
   * Human-readable file type name
   */
  displayName: string;

  /**
   * Supported conversion formats for this file type
   */
  supportedFormats: ConversionFormat[];

  /**
   * Whether this file type can be streamed directly
   */
  streamable: boolean;

  /**
   * Whether this file type can be viewed inline in browser
   */
  inlineViewable: boolean;

  /**
   * Icon class or identifier
   */
  icon?: string;
}

// ============================================================================
// Rate Limiting
// ============================================================================

/**
 * Rate limit error response
 */
export interface RateLimitError extends ErrorResponse {
  /**
   * Seconds to wait before retrying
   */
  retryAfter?: string;
}
